// Tenantra Inc. - Dispute Detail Page
import { useEffect, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { formatDate } from '@/utils/date';
import { navigate } from '@/services/navigation';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import api, { apiHelpers } from '@/services/api';

const STATUS = {
  open: { label: 'Awaiting Response', dot: 'bg-amber-500', text: 'text-amber-600' },
  landlord_responded: { label: 'Response Submitted', dot: 'bg-blue-500', text: 'text-blue-600' },
  under_review: { label: 'Under Review', dot: 'bg-blue-500', text: 'text-blue-600' },
  resolved: { label: 'Resolved', dot: 'bg-emerald-500', text: 'text-emerald-600' }
};

const DECISIONS = {
  remove_entry: { label: 'Entry Removed', text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
  keep_entry: { label: 'Entry Kept', text: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  modify_entry: { label: 'Entry Modified', text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' }
};

const REASONS = {
  not_my_tenancy: 'Not My Tenancy', incorrect_dates: 'Incorrect Dates',
  incorrect_amount: 'Incorrect Amount', damage_not_caused: 'Damage Not Caused',
  payment_was_made: 'Payment Was Made', other: 'Other Reason'
};

export default function DisputeDetailPage() {
  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [response, setResponse] = useState('');
  const [showForm, setShowForm] = useState(false);

  const disputeId = window.location.pathname.split('/').pop();

  useEffect(() => { if (disputeId) fetchDispute(); }, [disputeId]);

  const fetchDispute = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/disputes/${disputeId}`);
      setDispute(res.data);
      if (res.data.landlord_response) setResponse(res.data.landlord_response);
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
      navigate('/landlord/disputes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (response.length < 10) { toast.error('Response must be at least 10 characters'); return; }
    try {
      setSubmitting(true);
      await api.post(`/disputes/${disputeId}/respond`, { response, evidence_file_ids: [] });
      toast.success('Response submitted');
      fetchDispute();
      setShowForm(false);
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <TableSkeleton rows={5} spans={[5, 3, 2, 2]} />;
  }

  if (!dispute) {
    return (
      <div className="text-center py-20">
        <p className="text-sm text-slate-500 mb-3">Dispute not found</p>
        <a href="/landlord/disputes" className="text-xs text-teal-600 font-medium">Back to Disputes</a>
      </div>
    );
  }

  const s = STATUS[dispute.status] || STATUS.open;
  const decision = dispute.admin_decision ? DECISIONS[dispute.admin_decision] : null;
  const canRespond = dispute.status === 'open' || dispute.status === 'landlord_responded';

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-0 pb-8" data-testid="dispute-detail">
      {/* Back + Header */}
      <a href="/landlord/disputes" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-3">
        <ArrowLeft className="w-3 h-3" /> Back to Disputes
      </a>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-sm font-semibold text-slate-900">Dispute Details</h1>
          <p className="text-xs text-slate-500">{dispute.property_address}</p>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${s.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
          {s.label}
        </span>
      </div>

      {/* Status notice */}
      {dispute.status === 'open' && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 mb-4">
          <p className="text-xs text-amber-800">A tenant has disputed your report. Please review and respond.</p>
        </div>
      )}
      {dispute.status === 'landlord_responded' && (
        <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2.5 mb-4">
          <p className="text-xs text-blue-800">Your response has been submitted. Waiting for admin review.</p>
        </div>
      )}

      {/* Resolution */}
      {dispute.status === 'resolved' && decision && (
        <div className={`${decision.bg} border ${decision.border} rounded-md px-3 py-2.5 mb-4`}>
          <p className={`text-xs font-medium ${decision.text} mb-0.5`}>{decision.label}</p>
          {dispute.admin_notes && <p className="text-xs text-slate-600">{dispute.admin_notes}</p>}
        </div>
      )}

      {/* Tenant's Dispute + Report Side by Side */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        {/* Tenant's Dispute */}
        <div className="border border-slate-200 rounded-md" data-testid="tenant-dispute-card">
          <div className="px-3 py-2 border-b border-slate-100">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest">Tenant's Dispute</span>
          </div>
          <div className="p-3 space-y-2.5">
            <div>
              <p className="text-[11px] text-slate-500">Tenant</p>
              <p className="text-xs font-medium text-slate-800">{dispute.tenant_name}</p>
            </div>
            <div>
              <p className="text-[11px] text-slate-500">Reason</p>
              <p className="text-xs font-medium text-slate-800">{REASONS[dispute.reason] || dispute.reason}</p>
            </div>
            <div>
              <p className="text-[11px] text-slate-500">Explanation</p>
              <p className="text-xs text-slate-700 whitespace-pre-wrap">{dispute.explanation}</p>
            </div>
            <div className="pt-2 border-t border-slate-100">
              <p className="text-[11px] text-slate-500">Filed {formatDate(dispute.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Disputed Report */}
        <div className="border border-slate-200 rounded-md" data-testid="disputed-report-card">
          <div className="px-3 py-2 border-b border-slate-100">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest">Disputed Report</span>
          </div>
          <div className="p-3 space-y-2.5">
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <p className="text-[11px] text-slate-500">Period</p>
                <p className="text-xs font-medium text-slate-800">{dispute.report_period}</p>
              </div>
              <div>
                <p className="text-[11px] text-slate-500">Property</p>
                <p className="text-xs font-medium text-slate-800 truncate">{dispute.property_address}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-slate-50 rounded px-2 py-1.5">
                <p className="text-[11px] text-slate-500">Payment</p>
                <p className="text-xs text-slate-800 capitalize">{dispute.report_payment_status?.replace('_', ' ')}</p>
              </div>
              <div className="bg-slate-50 rounded px-2 py-1.5">
                <p className="text-[11px] text-slate-500">Lease</p>
                <p className="text-xs text-slate-800 capitalize">{dispute.report_lease_status?.replace('_', ' ')}</p>
              </div>
              <div className="bg-slate-50 rounded px-2 py-1.5">
                <p className="text-[11px] text-slate-500">Issues</p>
                <p className={`text-xs ${dispute.report_issues_count > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {dispute.report_issues_count || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Your Response */}
      <div className="border border-slate-200 rounded-md mb-4" data-testid="response-section">
        <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest">Your Response</span>
          {canRespond && !showForm && (
            <button onClick={() => setShowForm(true)} className="text-[11px] text-teal-600 font-medium" data-testid="add-response-btn">
              {dispute.landlord_response ? 'Edit' : 'Add Response'}
            </button>
          )}
        </div>

        {dispute.landlord_response && !showForm ? (
          <div className="p-3">
            <p className="text-xs text-slate-700 whitespace-pre-wrap">{dispute.landlord_response}</p>
            <p className="text-[11px] text-slate-500 mt-2">Submitted {formatDate(dispute.updated_at)}</p>
          </div>
        ) : showForm ? (
          <div className="p-3 space-y-3">
            <Textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder="Provide your response to this dispute..."
              className="text-xs border-slate-200 min-h-[100px]"
              disabled={submitting}
              data-testid="response-textarea"
            />
            <p className="text-[11px] text-slate-500">{response.length}/10 minimum characters</p>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleSubmit}
                disabled={response.length < 10 || submitting}
                className="bg-teal-600 hover:bg-teal-700 text-white text-[11px] font-medium h-8 px-4"
                data-testid="submit-response-btn"
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Submit Response'}
              </Button>
              <button
                onClick={() => { setShowForm(false); setResponse(dispute.landlord_response || ''); }}
                className="text-[11px] text-slate-400 font-medium"
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="p-3">
            <p className="text-xs text-slate-500">
              {canRespond ? "No response yet. Click 'Add Response' to submit your side." : "No response was submitted."}
            </p>
          </div>
        )}
      </div>

      {/* Tips - compact, no icons */}
      {canRespond && (
        <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-1.5">Tips</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Be factual and professional. Reference specific dates, amounts, or events. Include relevant documentation. Your response will be reviewed by an admin.
          </p>
        </div>
      )}
    </div>
  );
}
